import { useEffect, useMemo, useState } from 'react';
import { ClipboardIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { FormField } from '../components/shared/form';
import { useToast } from '../contexts/ToastContext';
import { useFlag } from '../hooks/useFeatureFlags';
import {
  accountValidationErrors,
  useAccount,
  useUpdateAccount,
  type AccountUpdate,
} from '../hooks/useAccount';
import { PublishingVersionsSection } from '../components/versioning/PublishingVersionsSection';
import { EnvironmentsSection } from '../components/environments/EnvironmentsSection';
import { CustomDomainsSection } from '../components/domains/CustomDomainsSection';

/**
 * Account · General (task 633) — the AMS account's home in the UI.
 *
 * Until now the account had no visible home; the slug only peeked out of the
 * avatar dropdown. This page is a read-only projection of the account that AMS
 * owns, edited through the innovate-service self-account proxy
 * (`GET/PATCH /api/account`). Nothing is read from the JWT.
 *
 * Two states, per the approved view-first design (kv 2026-08-18):
 *  1. VIEW (default, everyone): the account name and Account ID in the header —
 *     the ID as plain, borderless mono text with a copy affordance — and a
 *     view-grid of Account name + URL Slug with the endpoint-URL hint.
 *  2. EDIT (owner/admin only): the name and URL Slug become the only two
 *     inputs; Save stays disabled until a value actually differs; the slug
 *     field carries a standing amber warning, and saving a *changed* slug goes
 *     through a type-to-confirm dialog because it breaks every published
 *     endpoint URL. Editor/viewer never see the Edit button.
 *
 * Stale-claims note: a slug change is authoritative on the server immediately
 * (published URLs move at once), but the `account_slug` JWT claim — and every
 * surface that reads it — lags until the next token refresh / re-login. The
 * confirm dialog and the success toast both say so.
 */

/** Trim, treating an all-whitespace value as empty. */
const clean = (value: string): string => value.trim();

export default function AccountGeneralPage() {
  const toast = useToast();
  const { data: account, isLoading, isError, refetch } = useAccount();
  const updateAccount = useUpdateAccount();

  // Managing the account (member roles, invitations, and the account name /
  // URL slug) is gated by innovate.fe.account.manage, whose role-admin +
  // role-owner cohorts carry the rule (task 692). This restores the admin
  // capability the Phase 1 role collapse orphaned: the old `owner || admin`
  // checks named a role the catalog no longer emits, so they had silently
  // become owner-only.
  const canEdit = useFlag('innovate.fe.account.manage');

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Seed the inputs from stored state when entering edit mode, and re-seed
  // whenever fresh server state arrives while viewing (never while editing, so
  // a save that returns normalised values re-seeds only after we exit edit).
  useEffect(() => {
    if (account && !isEditing) {
      setName(account.name);
      setSlug(account.slug);
    }
  }, [account, isEditing]);

  const nameChanged = account ? clean(name) !== account.name : false;
  const slugChanged = account ? clean(slug) !== account.slug : false;
  const hasChanges = nameChanged || slugChanged;

  // Per-field messages from the last rejected save, keyed by field name.
  const fieldErrors = useMemo(() => {
    return accountValidationErrors(updateAccount.error).reduce<Record<string, string>>(
      (acc, { field, message }) => {
        acc[field] = message;
        return acc;
      },
      {},
    );
  }, [updateAccount.error]);

  const saveFailedWithoutFields =
    updateAccount.isError && Object.keys(fieldErrors).length === 0;

  /** Only the fields that actually changed are sent. */
  const buildUpdate = (): AccountUpdate => {
    const update: AccountUpdate = {};
    if (nameChanged) update.name = clean(name);
    if (slugChanged) update.slug = clean(slug);
    return update;
  };

  const submit = () => {
    if (!hasChanges || updateAccount.isPending) return;
    const slugWasChanged = slugChanged;

    updateAccount.mutate(buildUpdate(), {
      onSuccess: () => {
        setConfirmOpen(false);
        setIsEditing(false);
        if (slugWasChanged) {
          toast.warning(
            'Account slug changed',
            'Your published endpoint URLs use the new slug immediately. The slug shown elsewhere in the app may lag until you sign in again.',
          );
        } else {
          toast.success('Account saved', 'Your account details were updated.');
        }
      },
      onError: (error) => {
        // Per-field reasons render against the inputs; a slug uniqueness/format
        // rejection keeps the confirm dialog open so the User can correct it.
        if (accountValidationErrors(error).length === 0) {
          setConfirmOpen(false);
          toast.error('Account not saved', 'Your changes could not be saved. Please try again.');
        }
      },
    });
  };

  /** Save: a *changed* slug requires the type-to-confirm dialog first. */
  const handleSave = () => {
    if (!hasChanges || updateAccount.isPending) return;
    if (slugChanged) {
      setConfirmOpen(true);
      return;
    }
    submit();
  };

  const startEditing = () => {
    if (!account) return;
    setName(account.name);
    setSlug(account.slug);
    updateAccount.reset();
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setConfirmOpen(false);
    setIsEditing(false);
    if (account) {
      setName(account.name);
      setSlug(account.slug);
    }
    updateAccount.reset();
  };

  const copyAccountId = async () => {
    if (!account) return;
    try {
      await navigator.clipboard?.writeText(account.id);
      toast.info('Account ID copied', account.id);
    } catch {
      toast.error('Could not copy', 'Copy the Account ID manually.');
    }
  };

  return (
    <div>
      <PageHeader
        title="Account"
        description="Your account's name and URL slug — shared by everyone on the account, and used across Monarch Works and on your invoices."
      />

      <div className="mt-6 max-w-2xl">
        {isLoading && (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="account-loading">
            Loading your account…
          </p>
        )}

        {isError && !isLoading && (
          <div data-testid="account-error">
            <p className="text-sm text-red-600 dark:text-red-400">
              Your account is unavailable right now. Please try again.
            </p>
            <button
              onClick={() => refetch()}
              data-testid="account-retry"
              className="mt-2 inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {account && (
          <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
            {/* Header: account name + borderless mono Account ID + copy, Edit top-right */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h2
                    className="text-lg font-semibold text-gray-900 dark:text-white"
                    data-testid="account-name-view"
                  >
                    {account.name}
                  </h2>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-mono" data-testid="account-id">
                      ID {account.id}
                    </span>
                    <button
                      type="button"
                      onClick={copyAccountId}
                      data-testid="account-copy-id"
                      aria-label="Copy Account ID"
                      title="Copy Account ID"
                      className="rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                      <ClipboardIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  The account your team, AI Services, and billing all belong to.
                </p>
              </div>

              {canEdit && !isEditing && (
                <button
                  type="button"
                  onClick={startEditing}
                  data-testid="account-edit"
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-md transition-colors"
                >
                  Edit
                </button>
              )}
            </div>

            {/* VIEW state */}
            {!isEditing && (
              <div className="mt-6" data-testid="account-view">
                <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-3 text-sm">
                  <dt className="text-gray-500 dark:text-gray-400">Account name</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{account.name}</dd>
                  <dt className="text-gray-500 dark:text-gray-400">URL Slug</dt>
                  <dd
                    className="font-mono text-gray-900 dark:text-white"
                    data-testid="account-slug-view"
                  >
                    {account.slug}
                  </dd>
                </dl>
                <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                  The URL Slug is part of every endpoint URL you publish (…/
                  <span className="font-mono font-semibold">{account.slug}</span>/&lt;endpoint&gt;).
                </p>
              </div>
            )}

            {/* EDIT state (owner/admin) */}
            {isEditing && (
              <form
                data-testid="account-edit-form"
                className="mt-6 space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                <FormField
                  label="Account name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={fieldErrors.name}
                  disabled={updateAccount.isPending}
                  className="max-w-md"
                />

                <div className="max-w-md">
                  <label
                    htmlFor="slug"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    URL Slug
                  </label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    disabled={updateAccount.isPending}
                    data-testid="account-slug-input"
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm font-mono
                      ${
                        fieldErrors.slug
                          ? 'border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                      }
                      bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                      focus:outline-none focus:ring-1
                      disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed`}
                  />
                  {fieldErrors.slug ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.slug}</p>
                  ) : (
                    <p
                      className="mt-1 flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400"
                      data-testid="account-slug-warning"
                    >
                      <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Changing the slug breaks every published endpoint URL.
                    </p>
                  )}
                </div>

                {saveFailedWithoutFields && (
                  <p className="text-sm text-red-600 dark:text-red-400" data-testid="account-save-error">
                    Your changes could not be saved. Please try again.
                  </p>
                )}

                <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <button
                    type="button"
                    onClick={cancelEditing}
                    disabled={updateAccount.isPending}
                    data-testid="account-cancel"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!hasChanges || updateAccount.isPending}
                    data-testid="account-save"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {updateAccount.isPending ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </form>
            )}
          </section>
        )}

        {/* Publishing & versions (task 725) — the account-wide, one-way opt-in.
            It renders its own dormant/enabled states and is invisible when the
            `innovate.fe.resource-versioning` rollback lever is off. It does not
            depend on the account fetch above, so it stays on screen even when
            the account proxy is unavailable. */}
        <PublishingVersionsSection />

        {/* Deployment environments (task 760) — DIRECTLY under Publishing, and
            inert until Publishing is on. The adjacency is the explanation: an
            environment points at a published version, so it has nothing to
            serve until versions can be frozen. It owns its own flag, disabled
            state and fetch, exactly as the section above does. */}
        <EnvironmentsSection />

        {/* Custom domains (task 850) — an account-level alias of the whole
            endpoint surface family, so it belongs beside the account's slug
            rather than on any one AI Service. It owns its own flag, plan gate,
            fetch and polling, exactly as the two sections above own theirs. */}
        <CustomDomainsSection />
      </div>

      {/* Slug-change type-to-confirm dialog */}
      {confirmOpen && account && (
        <SlugChangeConfirmDialog
          newSlug={clean(slug)}
          isLoading={updateAccount.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={submit}
        />
      )}
    </div>
  );
}

interface SlugChangeConfirmDialogProps {
  newSlug: string;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * A deliberate speed bump for a destructive-adjacent change: the User must type
 * the new slug back before Confirm enables. Not a generic ConfirmDialog because
 * that one has no type-to-confirm input.
 */
function SlugChangeConfirmDialog({
  newSlug,
  isLoading,
  onCancel,
  onConfirm,
}: SlugChangeConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === newSlug;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="slug-confirm-title"
      data-testid="slug-confirm-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) onCancel();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-start gap-3 mb-4">
          <ExclamationTriangleIcon
            className="h-6 w-6 flex-shrink-0 text-amber-500"
            aria-hidden="true"
          />
          <h3
            id="slug-confirm-title"
            className="text-lg font-medium text-gray-900 dark:text-white"
          >
            Change the URL slug?
          </h3>
        </div>

        <div className="mb-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <p>
            Every published endpoint URL uses this slug. Changing it to{' '}
            <span className="font-mono font-semibold">{newSlug}</span> breaks every existing
            endpoint URL immediately — anyone calling the old URLs will get a 404.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The new slug takes effect on the server at once. The slug shown elsewhere in the app
            may lag until you sign in again.
          </p>
        </div>

        <label
          htmlFor="slug-confirm-input"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Type <span className="font-mono font-semibold">{newSlug}</span> to confirm
        </label>
        <input
          id="slug-confirm-input"
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={isLoading}
          data-testid="slug-confirm-input"
          autoComplete="off"
          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm sm:text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            data-testid="slug-confirm-cancel"
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!matches || isLoading}
            data-testid="slug-confirm-submit"
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving…' : 'Change slug'}
          </button>
        </div>
      </div>
    </div>
  );
}
